import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { Icon, colors } from '@iq/react-components';

import { DropdownMenu } from '../../../../DropdownMenu';
import { useDoubleClick, replacer } from '../../../../../utils';
import { STATUSES, RISK_SEVERITY_ORDER } from '../../../../../constants';
import { getLastVariableData } from '../../../../../bundles/variables';
import { getLinkedSources } from '../../../../../bundles/sources';
import { getAllIntegrations } from '../../../../../bundles/integrations';
import { getStateSets, findActiveStatesForVariables } from '../../../../../bundles/statesets';
import {
  getPageType,
  setActiveComponentId,
  setActiveModelId,
  setZoomComponentId,
} from '../../../../../bundles/application';
import { getIsTenantOrSuperAdmin } from '../../../../../bundles/auth';
import { useComponentPanel } from '../../ComponentPanelState';
import config from '../../../../../config';

// Factory that returns a React component rendering a status icon for a given status and layout
const makeStatusIconRenderer = (status, flat) => {
  const { color: statusColor, icon: statusIcon } = STATUSES[status?.status] || {};
  const [r, g, b] = statusColor || [0, 0, 0];
  let statusText = (!status?.errors && !status?.warns && status?.statusText) || '';
  if (status?.eventText) {
    statusText = status.eventText;
  }
  return function StatusIcon() {
    return (
      <div title={statusText}>
        <Icon
          icon={statusIcon}
          style={{ color: `rgba(${r}, ${g}, ${b}, ${statusColor ? 1 : 0})` }}
          className="selected"
          key={status?.id}
          size={flat ? 'm' : 's'}
        />
      </div>
    );
  };
};

// Factory that returns a React component rendering a state icon for given states and layout
const makeStateIconRenderer = (states, flat) => {
  const stateText = states?.length > 1 ? 'Multiple active states found' : states?.[0]?.text;
  const stateIcon = states?.length > 1 ? 'he-help' : states?.[0]?.icon || 'he-status';
  let stateColor = states?.length > 1 ? colors.StatusRed : states?.[0]?.color;
  if (states?.[0]?.quality !== undefined && states?.[0]?.quality !== 1) {
    stateColor += '33';
  }
  return function StateIcon() {
    return (
      <div title={stateText}>
        <Icon
          icon={stateIcon}
          style={{ color: stateColor }}
          key={states?.[0]?.id}
          size={flat ? 'm' : 's'}
        />
      </div>
    );
  };
};

// Simple icon renderer factory for the default icon
const makeSimpleIconRenderer = (iconName, flat) => () => (
  <Icon
    icon={iconName}
    size={flat ? 'm' : 's'}
  />
);

const DefaultStatusItem = () => (
  <Icon
    icon="blank"
    size="s"
  />
);

const TreeItem = React.forwardRef(
  (
    {
      depth,
      expanded = false,
      hasChildren,
      item,
      isSelected = false,
      toggleExpansion,
      treeRoot,
      children,
    },
    ref
  ) => {
    const {
      activeModelId,
      canDelete,
      autoLoad,
      canEdit,
      displayStatus: shouldDisplayStatus,
      flatList: flat,
      viewerReady,
      showModels,
      hasRootModel,
      activeComponentId,
      setCreating: onCreate,
      setDeleting: onDelete,
      setUpdating: onUpdate,
      tree: componentTree,
      disableAutoScroll,
      site,
    } = useComponentPanel();

    const dispatch = useDispatch();
    const isTenantAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, site?.org));
    const isOnPremUser = config?.onPremise && !isTenantAdmin;

    // Local click handlers that use dispatch
    const onComponentClick = (id) => {
      if (id === componentTree?.id) {
        dispatch(setActiveComponentId(null));
      } else {
        disableAutoScroll();
        dispatch(setActiveComponentId(id));
      }
      dispatch(setZoomComponentId(null));
    };

    const onComponentDoubleClick = (id) => {
      if (id !== activeComponentId) {
        onComponentClick(id);
      }
      dispatch(setZoomComponentId(id));
    };

    const onModelClick = (modelId) => {
      disableAutoScroll();
      dispatch(setActiveModelId(modelId));
    };
    const {
      id,
      itemDesignation,
      simpleDesignation,
      icon,
      name,
      models = [],
      variables = [],
      filteredStatus,
      ownStatus,
      stateset: areaState,
    } = item;

    const sources = useSelector(getLinkedSources);
    const integrations = useSelector(getAllIntegrations);
    const lastVariableData = useSelector(getLastVariableData);
    const stateSets = useSelector(getStateSets);
    const pageType = useSelector(getPageType);

    const [showItemMenu, setShowItemMenu] = useState(false);
    const [StatusIcon, setStatusIcon] = useState(() => DefaultStatusItem);
    const [menuPositionY, setMenuPositionY] = useState('top');
    const [itemBodyReference, itemBody] = useDoubleClick(() => onComponentDoubleClick(id));

    filteredStatus?.eventRiskStatus?.toSorted(
      (a, b) => RISK_SEVERITY_ORDER.indexOf(a.status) - RISK_SEVERITY_ORDER.indexOf(b.status)
    );

    const relevantHealthStatus = expanded && !flat ? ownStatus : filteredStatus;

    const activeStates = useMemo(
      () =>
        variables.length > 0
          ? findActiveStatesForVariables(variables, lastVariableData, stateSets)
          : [],
      [variables, lastVariableData, stateSets]
    );

    // status/state icon renderers are produced by module-level factories
    useEffect(() => {
      let iconSet = false;
      switch (pageType) {
        case 'health': {
          if (relevantHealthStatus && relevantHealthStatus.status >= 0) {
            setStatusIcon(() => makeStatusIconRenderer(relevantHealthStatus, flat));
            iconSet = true;
          }
          break;
        }
        case 'risk': {
          if (filteredStatus && filteredStatus?.eventRiskStatus?.length > 0) {
            setStatusIcon(() => makeStatusIconRenderer(filteredStatus.eventRiskStatus[0], flat));
            iconSet = true;
          }
          break;
        }
        case 'stateset': {
          if (areaState) {
            setStatusIcon(() => makeStateIconRenderer([areaState.states], flat));
            iconSet = true;
          } else if (activeStates.length > 0) {
            setStatusIcon(() => makeStateIconRenderer(activeStates, flat));
            iconSet = true;
          }
          break;
        }
        default:
      }
      if (iconSet === false) {
        setStatusIcon(() => makeSimpleIconRenderer(icon || 'he-dot', flat));
      }
    }, [
      expanded,
      JSON.stringify(filteredStatus),
      JSON.stringify(ownStatus),
      activeStates,
      pageType,
    ]);

    const checkActive = (modelId) => modelId === activeModelId;

    const handleExpandClick = (event) => {
      event.stopPropagation();
      toggleExpansion(id);
    };

    const handleComponentClick = (event, modelId, isActive) => {
      event.stopPropagation();
      onComponentClick(id);
      if (!isActive && modelId && viewerReady) {
        onModelClick(modelId);
      }
    };

    const handleModelClick = (event, modelId, isActive) => {
      event.stopPropagation();
      if (!modelId) return;

      const shouldHandleModelClick = !isActive && viewerReady;
      const shouldHandleComponentClick = !hasRootModel && activeComponentId !== id;

      if (shouldHandleModelClick || shouldHandleComponentClick) {
        if (shouldHandleComponentClick) {
          onComponentClick(id);
        }
        setShowItemMenu(false);
        onModelClick(modelId);
      }
    };

    const onUpdateComponent = () => {
      setShowItemMenu(false);
      onUpdate({ show: true, componentId: id });
    };

    const onCreateComponent = () => {
      setShowItemMenu(false);
      onCreate({
        show: true,
        parent: id,
        type: 'component',
      });
    };

    const onDeleteComponent = () => {
      setShowItemMenu(false);
      onDelete({ show: true, componentId: id });
    };

    const onIntegrationLink = (link) => {
      setShowItemMenu(false);
      window.open(link, '_blank');
    };

    const getMenuPosition = () => {
      if (treeRoot && itemBody) {
        const { height: rH, top: rT } = treeRoot.getBoundingClientRect();
        const { top: indexT } = itemBody.getBoundingClientRect();
        const repositionAt = rH * 0.5 + rT;

        if (indexT > repositionAt) {
          setMenuPositionY('top');
        } else {
          setMenuPositionY('bottom');
        }
      }
    };

    useEffect(() => {
      const handleScroll = () => setShowItemMenu(false);
      if (treeRoot) {
        treeRoot.addEventListener('scroll', handleScroll);
      }
      return () => {
        if (treeRoot) {
          treeRoot.removeEventListener('scroll', handleScroll);
        }
      };
    }, [treeRoot]);

    const onMenuOpen = () => {
      getMenuPosition();
      setShowItemMenu(true);
    };

    let ComponentIcon = () => (
      <Icon
        icon={icon || 'he-dot'}
        size={flat ? 'm' : 's'}
      />
    );
    if (shouldDisplayStatus) {
      ComponentIcon = StatusIcon;
    }

    const ToggleIcon = hasChildren
      ? () => (
          <div onClick={(event) => handleExpandClick(event)}>
            <Icon
              icon={expanded ? 'he-down' : 'he-forwards'}
              size="xs"
            />
          </div>
        )
      : () => <div></div>;

    const buildModelOptions = () =>
      models.map((m) => {
        const iconType = m.modelType === '2d' ? 'avt-2d' : 'avt-3d';
        const modelIsActive = checkActive(m.modelId);

        const optionClasses = classnames('tree-item-model', {
          active: modelIsActive,
          'viewer-loading': !viewerReady,
        });

        return {
          component: (
            <div
              key={m.modelId}
              className={optionClasses}
              onClick={(event) => handleModelClick(event, m.modelId, modelIsActive)}
            >
              <div>{m.customName || m.name}</div>
              <Icon
                icon={iconType}
                size="s"
              />
            </div>
          ),
        };
      });

    const itemMenuOptions = useMemo(() => {
      const options = [];

      if (models.length > 0 && showModels) {
        options.push({ options: buildModelOptions() });
      }

      const componentOptions = [];
      if (canEdit) {
        componentOptions.push(
          { component: <div>Edit Component</div>, onSelect: onUpdateComponent },
          { component: <div>Add Component</div>, onSelect: onCreateComponent }
        );
      }

      if (canDelete && !item.integrationOptions) {
        componentOptions.push({
          component: <div>Delete Component</div>,
          onSelect: onDeleteComponent,
        });
      }
      if (componentOptions.length > 0) {
        options.push({ options: componentOptions });
      }

      const deeplinks = [];
      if (item.sourceConfig?.hasSourceLink) {
        for (const source of sources) {
          const { domain, componentSourceLink } = source.options;
          const link = componentSourceLink
            .replace('<domain>', domain)
            .replace('<assetId>', item.sourceConfig.assetId);

          deeplinks.push({
            component: <div className="tree-item-deeplink">Detailed asset information</div>,
            onSelect: () => onIntegrationLink(link),
          });
        }
      }
      if (item.integrationOptions) {
        for (const [k, v] of Object.entries(item.integrationOptions)) {
          const integration = integrations.find((index) => index.id === k);
          const { endpoint, enabled } = integration?.configuration?.deeplinks?.componentLink || {};
          let link;
          if (endpoint && enabled) {
            try {
              link = endpoint.replaceAll(/({\w+})/g, (match) => replacer(match, v));
              deeplinks.push({
                component: (
                  <div className="tree-item-deeplink">{`Asset information (${integration.name})`}</div>
                ),
                onSelect: () => onIntegrationLink(link),
              });
            } catch (error) {
              console.error(error);
            }
          }
        }
      }
      if (deeplinks.length > 0) {
        options.push({ options: deeplinks });
      }

      return options;
    }, [item, showModels, models, canEdit, canDelete, sources, integrations]);

    const defaultModelId = models.length > 0 && models[0].modelId;
    const models3dIds = models.filter((m) => m.modelType !== '2d').map((m) => m.modelId);
    const models2dIds = models.filter((m) => m.modelType === '2d').map((m) => m.modelId);
    const hasActive3d = models3dIds.includes(activeModelId);
    const hasActive2d = models2dIds.includes(activeModelId);
    const [model3dId] = models3dIds;
    const [model2dId] = models2dIds;
    const hasActiveModel = hasActive3d || hasActive2d;

    const contentClasses = classnames('tree-item-content', {
      [`level-${depth}`]: !flat,
      'flat-list': flat,
      leaf: !hasChildren,
      'menu-active': showItemMenu,
      selected: isSelected,
    });
    const model3dClasses = classnames('tree-item-model', {
      active: hasActive3d,
      'viewer-loading': !viewerReady,
    });
    const model2dClasses = classnames('tree-item-model', {
      active: hasActive2d,
      'viewer-loading': !viewerReady,
    });

    return (
      <div
        className="tree-item"
        role="tree-item"
        ref={(element) => ref(element, id)}
      >
        <div
          className={contentClasses}
          onClick={(event) =>
            handleComponentClick(event, autoLoad ? defaultModelId : null, hasActiveModel)
          }
          ref={itemBodyReference}
        >
          <div className="content-left">
            {!flat && <ToggleIcon />}
            <div className="component-type">
              <ComponentIcon />
            </div>
            <div className="tree-item-name">
              {flat ? (
                <>
                  <div
                    key={`${id}-ng-1`}
                    className="tree-item-name-long"
                  >
                    {name}
                  </div>
                  <div key={`${id}-ng-2`}>{itemDesignation}</div>
                </>
              ) : (
                <>
                  <div key={`${id}-ng-1`}>{simpleDesignation || itemDesignation}</div>
                  <div
                    key={`${id}-ng-2`}
                    className="tree-item-name-long"
                  >
                    {name}
                  </div>
                  {showModels ? (
                    <>
                      {model3dId && (
                        <div className={model3dClasses}>
                          <Icon
                            key={`${id}-ng-3`}
                            icon="avt-3d"
                            onClick={(event) => handleModelClick(event, model3dId, hasActive3d)}
                            size="s"
                          />
                        </div>
                      )}
                      {model2dId && (
                        <div className={model2dClasses}>
                          <Icon
                            key={`${id}-ng-4`}
                            icon="avt-2d"
                            onClick={(event) => handleModelClick(event, model2dId, hasActive2d)}
                            size="s"
                          />
                        </div>
                      )}
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div className={flat ? 'content-right status-icon-padding' : 'content-right'}>
            {!isOnPremUser && (
              <DropdownMenu
                trigger="he-moreoptionshorizontal"
                triggerIconColor={isSelected ? '#fff' : undefined}
                menuOptions={itemMenuOptions}
                className="tree-item-menu"
                menuXPlacement="left"
                menuYPlacement="bottom"
                onMenuOpen={onMenuOpen}
                onMenuClose={() => setShowItemMenu(false)}
                styles={{
                  dropdownIndicator: { padding: '0 0.5rem', filter: 'none' },
                  option: { cursor: 'pointer' },
                  container: { position: 'absolute' },
                  menu: {
                    minWidth: 'auto',
                    top: menuPositionY === 'bottom' ? '100%' : 'auto',
                    bottom: menuPositionY === 'bottom' ? 'auto' : '100%',
                  },
                }}
              />
            )}
          </div>
        </div>
        {expanded && children}
      </div>
    );
  }
);

export default TreeItem;
